import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectGroup,
	SelectItem,
} from '@worldcoo/ui/components/select';
import { SpanishIcon } from './assets/languages/spanish-icon';
import { CatalanIcon } from './assets/languages/catalan-icon';
import { EnglishIcon } from './assets/languages/english-icon';
import { ItalianIcon } from './assets/languages/italian-icon';
import { DeutchIcon } from './assets/languages/deutch-icon';
import { FrenchIcon } from './assets/languages/french-icon';
import { NederlandsIcon } from './assets/languages/nederlands-icon';
import { PortugueseIcon } from './assets/languages/portuguese-icon';
import { useTranslation } from '@/lib/i18n';
import { Suspense } from 'react';
import { localeToLanguage } from '@worldcoo/monolith/core';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePortalMetadataQuery } from '@/lib/queries';
import { EuskeraIcon } from './assets/languages/euskera-icon';
import { GalegoIcon } from './assets/languages/galego-icon';
import { isNotFound } from '@tanstack/react-router';

const languageOptions: { value: PortalLocale; label: string }[] = [
	{ value: 'ca', label: 'Català' },
	{ value: 'de', label: 'Deutsch' },
	{ value: 'en', label: 'English' },
	{ value: 'es', label: 'Español' },
	{ value: 'eu', label: 'Euskera' },
	{ value: 'fr', label: 'Français' },
	{ value: 'gl', label: 'Galego' },
	{ value: 'it', label: 'Italiano' },
	{ value: 'nl', label: 'Nederlands' },
	{ value: 'pt', label: 'Português' },
];

const languageFlags = {
	ca: <CatalanIcon />,
	de: <DeutchIcon />,
	en: <EnglishIcon />,
	es: <SpanishIcon />,
	eu: <EuskeraIcon />,
	fr: <FrenchIcon />,
	gl: <GalegoIcon />,
	it: <ItalianIcon />,
	nl: <NederlandsIcon />,
	pt: <PortugueseIcon />,
};

export function LanguageSelect(props: { className?: string }) {
	return (
		<Suspense fallback={<LanguageSelectPrimitive languageOptions={languageOptions} />}>
			<SuspensedLanguageSelect className={props.className} />
		</Suspense>
	);
}

function SuspensedLanguageSelect(props: { className?: string }) {
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<LanguageSelectPrimitive
			languageOptions={languageOptions.filter((option) => metadata.languages.includes(localeToLanguage[option.value]))}
			className={props.className}
		/>
	);
}

function LanguageSelectPrimitive(props: { languageOptions: typeof languageOptions; className?: string }) {
	const { i18n, language, changeLanguage } = useTranslation();
	if (props.languageOptions.length <= 1) return;

	return (
		<Select onValueChange={changeLanguage} value={language}>
			<SelectTrigger
				size="lg"
				className={cn('bg-transparent! border-none text-base shadow-none', props.className)}
				aria-label={i18n.actions.selectLanguage}
			>
				<SelectValue className="gap-2">
					{languageFlags[language]}
					{props.languageOptions.find((option) => option.value === language)?.label}
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					{props.languageOptions.map(({ value, label }) => (
						<SelectItem key={value} value={value}>
							{languageFlags[value]}
							{label}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}
